import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useMapSpot } from '../../context/MapSpotContext'
import { useFavorites } from '../useFavorites'
import { useEntitySearch } from '../useEntitySearch'
import { useGeocoding } from '../useGeocoding'
import { useDistanceUnit } from '../useDistanceUnit'
import { useUserStats } from '../useUserStats'
import { useMapLocation } from '../useMapLocation'
import { useMapFiltersWithToggle } from '../useMapFiltersWithToggle'
import { useAuthForms } from '../useAuthForms'
import { useContactForm } from '../useContactForm'
import { useConfirmations } from '../useConfirmations'
import { useMapHandlers } from '../useMapHandlers'
import { useSpotMode } from '../useSpotMode'
import { useSharedMapState } from './useSharedMapState'
import { useEntityMode } from './useEntityMode'
import { useSpotModeState } from './useSpotModeState'
import { useMapConfiguration } from './useMapConfiguration'
import { useEntityFetching } from '../useEntityFetching'
import { useSpotModeEffects } from '../useSpotModeEffects'
import { useSpotModeActivation } from '../useSpotModeActivation'
import { useMapPersistence } from '../useMapPersistence'
import { useEntitySelection } from '../useEntitySelection'
import { usePanelState } from '../usePanelState'
import { useEntityLocations } from '../useEntityLocations'
import { usePanelStateSync } from './usePanelStateSync'
import { useMapRefetchOnNavigation } from './useMapRefetchOnNavigation'
import { useUserLocationInit } from './useUserLocationInit'
import { useEntityDetailEffects } from './useEntityDetailEffects'
import { handleCurrentLocationClick } from '../../utils/mapHandlerUtils'
import { applyMarkerOffsets } from '../../pages/map/utils'
import { trackEntityView, trackFirstEntityView } from '../../utils/analytics'
import { useOpenNowFilter } from '../useOpenNowFilter'

/**
 * Custom hook that encapsulates all state management for EntityDetailMap
 * This reduces the main component from 1058 lines to under 300 lines
 */
export function useEntityDetailMapState({
  entityId,
  isImmersiveMode,
  onTabChange,
  panelState,
  activeTab,
  initialChannelIdProp,
  entityDetails
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAuthenticated, logout } = useAuth()
  const { convertFromKm } = useDistanceUnit()
  
  // State to store channelId for auto-opening channel when navigating to contact tab
  const [initialChannelId, setInitialChannelId] = useState(initialChannelIdProp)
  const { isSpotModeActive, activateSpotMode, deactivateSpotMode, preSelectedEntity, spotModeCallback, initialLocation } = useMapSpot()
  
  // Entity-specific mode: when entityId is provided, show only sightings for that entity
  const isEntityMode = entityId !== null

  // Use shared map state hook
  const {
    entities,
    setEntities,
    userLocation,
    setUserLocation,
    radius,
    setRadius,
    searchLocation,
    setSearchLocation,
    searchLocationName,
    setSearchLocationName,
    showUserLocation,
    setShowUserLocation,
    isLoading,
    setIsLoading,
    mapRef,
    markerRefs,
    currentOpenPopupRef,
    hasInitialized,
    hasShownLocationError,
    previousLocationKey,
    savedMapViewRef,
    activeMapLocation
  } = useSharedMapState({ isEntityMode: true, entityId })
  
  // Use entity mode hook for entity-specific state
  const {
    entityGallery,
    setEntityGallery,
    galleryRenderKey,
    setGalleryRenderKey,
    refreshGallery,
    confirmingSighting,
    setConfirmingSighting
  } = useEntityMode()
  
  // Favorites hook
  const { isFavorite, toggleFavorite, isTogglingFavorite, favorites, updateFavoriteNotify } = useFavorites(user, userLocation)
  
  // Map state persistence
  const storageKey = isEntityMode ? `mapState_entity_${entityId}` : 'mapState_home_v2'
  const { persistedState } = useMapPersistence({
    storageKey,
    shouldPersist: !isEntityMode,
    stateToSave: {
      entities,
      userLocation,
      radius,
      searchLocation,
      searchLocationName
    },
    dependencies: [entities, userLocation, radius, searchLocation, searchLocationName]
  })

  // Use geocoding hook
  const { geocode, reverseGeocode: reverseGeocodeFn, isGeocoding } = useGeocoding()

  // Location source tracking for active state feedback
  const [locationSource, setLocationSource] = useState(null)

  // Entity selection and panel state
  const {
    entitySearchQuery,
    setEntitySearchQuery,
    selectedEntityId,
    setSelectedEntityId,
    focusedEntityId,
    setFocusedEntityId,
    isolatedEntityId,
    setIsolatedEntityId
  } = useEntitySelection()

  const {
    isSpotPanelMinimized,
    setIsSpotPanelMinimized,
    isSearchPanelMinimized,
    setIsSearchPanelMinimized
  } = usePanelState()

  // Entity search
  const {
    results: entitySearchResults,
    isSearching,
    showDropdown: showSearchDropdown,
    setShowDropdown: setShowSearchDropdown
  } = useEntitySearch({
    query: entitySearchQuery,
    userLocation,
    radius,
    searchLocation
  })

  // Spot mode state
  const {
    spotModeLocation,
    setSpotModeLocation,
    spotModeEntity,
    setSpotModeEntity,
    spotModeEntityType,
    setSpotModeEntityType,
    spotModeNotes,
    setSpotModeNotes,
    spotModeEntityStatus,
    setSpotModeEntityStatus,
    spotModeImages,
    setSpotModeImages,
    isSubmittingSpot,
    setIsSubmittingSpot,
    entitySearchInputRef,
    entitySearchInputRefAlt,
    showShareOptions,
    setShowShareOptions,
    spotModeStartTime,
    setSpotModeStartTime,
    spotModeLocationSetTime,
    setSpotModeLocationSetTime,
    spotModeSocials,
    setSpotModeSocials
  } = useSpotModeState()

  // Panel state sync
  usePanelStateSync({
    isImmersiveMode,
    panelState,
    activeTab,
    setIsSearchPanelMinimized
  })

  // Auth and contact forms
  const {
    loginFormData,
    loginErrors,
    handleLoginChange,
    handleLoginSubmit,
    registerFormData,
    registerErrors,
    handleRegisterChange,
    handleRegisterSubmit,
    isAuthLoading,
    googleAuth
  } = useAuthForms(t, null, isImmersiveMode, onTabChange)

  const {
    contactFormData,
    contactErrors,
    isContactSubmitting,
    contactSuccess,
    handleContactChange,
    handleContactSubmit,
    setContactSuccess
  } = useContactForm(t)

  const {
    pendingConfirmations,
    loadingConfirmations,
    confirmingId,
    loadPendingConfirmations,
    handleConfirmSighting: handleConfirmSightingBase
  } = useConfirmations(user, t, () => {
    if (entityId) {
      fetchEntityLocations(entityId, null, null, true)
    }
  }, activeMapLocation, !isEntityMode || activeTab === 'auth')

  // User statistics — deferred in entity mode until auth tab is opened
  const { stats, loading: statsLoading, refetch: refetchStats } = useUserStats(user?.id, !!user && (!isEntityMode || activeTab === 'auth'), activeMapLocation)

  // Data fetching
  const { fetchNearbyEntities } = useEntityFetching(
    t,
    radius,
    setIsLoading,
    setEntities
  )

  // Map location hooks
  const { getUserLocation: getUserLocationBase, getLastSavedLocation, saveLocation, getDefaultLocation } = useMapLocation({ 
    t, 
    fetchCallback: null,
    reverseGeocode: reverseGeocodeFn 
  })

  // Wrap getUserLocation to track location source
  const getUserLocation = useCallback((options) => {
    return getUserLocationBase({
      ...options,
      onSuccess: (location, locationName) => {
        setLocationSource('geolocation')
        if (options?.onSuccess) {
          options.onSuccess(location, locationName)
        }
      }
    })
  }, [getUserLocationBase, setLocationSource])

  // Handler for "Use current location" button
  const handleCurrentLocation = useCallback(() => {
    handleCurrentLocationClick({
      getUserLocation,
      setUserLocation,
      setShowUserLocation,
      setIsSearchPanelMinimized,
      mapRef,
      isEntityMode: true,
      isGeolocationAvailable: true,
      isGettingLocation: false
    })
  }, [getUserLocation, setUserLocation, setShowUserLocation, setIsSearchPanelMinimized])

  // Map filters
  // Entity detail has no open-now toggle — skip the fetch entirely in entity mode
  const { openNowIds, openNowActive, toggleOpenNow } = useOpenNowFilter(!isEntityMode)
  const { filteredEntities, activeFilters, handleFilterToggle } = useMapFiltersWithToggle(entities, isEntityMode ? null : isolatedEntityId, openNowIds, openNowActive)

  // Spot mode lifecycle effects
  // Wrapper so useSpotMode / useSpotModeEffects can trigger GPS with no arguments and
  // still have the result update userLocation state (required for reactive re-centering).
  const triggerSpotGeolocation = useCallback(() => {
    getUserLocation({
      onSuccess: (location) => setUserLocation(location)
    })
  }, [getUserLocation, setUserLocation])

  useSpotModeEffects({
    isSpotModeActive,
    userLocation,
    preSelectedEntity,
    spotModeEntity,
    initialLocation,
    spotModeLocation,
    isFullscreenMode: false,
    isLoading,
    mapRef,
    savedMapViewRef,
    currentOpenPopupRef,
    setSpotModeEntity,
    setEntitySearchQuery,
    setUserLocation,
    setSpotModeLocation,
    setSpotModeNotes,
    setSpotModeEntityStatus,
    setIsSpotPanelMinimized,
    getUserLocation: triggerSpotGeolocation
  })

  const { defaultCenter, mapCenter, mapZoom, mapBounds } = useMapConfiguration({
    userLocation,
    searchLocation,
    entityLocation: null,
    radius,
    isEntityMode,
    entities,
    getDefaultLocation
  })

  // Set entities with offset
  const handleSetEntities = useCallback((locations) => {
    const offsetLocations = applyMarkerOffsets(locations)
    setEntities(offsetLocations)
  }, [setEntities])

  // Entity location fetching
  const { fetchEntityLocations } = useEntityLocations({
    entityId,
    entityName: entityDetails?.name,
    setIsLoading,
    setEntities: handleSetEntities
  })

  // User location initialization
  useUserLocationInit({
    isEntityMode,
    hasInitialized,
    userLocation,
    setUserLocation,
    getUserLocation,
    getLastSavedLocation,
    saveLocation,
    fetchNearbyEntities
  })

  // Entity mode initialization - separate from homepage initialization
  const hasInitializedEntityMode = useRef(false)
  useEffect(() => {
    if (!isEntityMode) return
    if (hasInitializedEntityMode.current) return
    hasInitializedEntityMode.current = true
    fetchEntityLocations(entityId, null, null, false)
    if (entityId) {
      trackEntityView(entityId, entityDetails?.name)
      trackFirstEntityView(entityId, entityDetails?.name)
    }
  }, [isEntityMode, fetchEntityLocations, entityId, entityDetails?.name])
  
  // Reset entity mode initialization when entityId changes
  useEffect(() => {
    if (isEntityMode) {
      hasInitializedEntityMode.current = false
    }
  }, [entityId, isEntityMode])

  // Map refetch on navigation
  useMapRefetchOnNavigation({
    userLocation: isEntityMode ? null : userLocation,
    fetchCallback: isEntityMode ? 
      (() => fetchEntityLocations(entityId, null, null, true)) : 
      ((loc) => fetchNearbyEntities(loc, null, true)),
    location,
    hasInitialized: isEntityMode ? hasInitializedEntityMode : hasInitialized,
    previousLocationKey,
    forceFresh: true
  })

  // Consolidated entity detail effects
  useEntityDetailEffects({
    initialChannelIdProp,
    setInitialChannelId,
    onTabChange,
    isImmersiveMode,
    showUserLocation,
    entityId,
    setEntityGallery,
    setGalleryRenderKey
  })

  // Map interaction handlers
  const {
    handleRadiusChange,
    geocodeAddress: geocodeAddressBase,
    handleSelectEntity
  } = useMapHandlers({
    userLocation,
    setUserLocation,
    setSearchLocationName,
    fetchNearbyEntities,
    geocode,
    searchLocation,
    entities,
    setEntities,
    setSelectedEntityId,
    setEntitySearchQuery,
    setShowSearchDropdown,
    mapRef,
    isImmersiveMode,
    setRadius
  })

  // Wrap geocodeAddress to track location source
  const geocodeAddress = useCallback(async () => {
    await geocodeAddressBase()
    setLocationSource('geocoded')
  }, [geocodeAddressBase, setLocationSource])

  // Spot mode handlers
  const {
    handleSpotModeActivation: baseHandleSpotModeActivation,
    handleSpotSubmit: baseHandleSpotSubmit
  } = useSpotMode({
    user,
    activateSpotMode,
    deactivateSpotMode,
    spotModeEntity,
    spotModeLocation,
    spotModeNotes,
    spotModeEntityStatus,
    spotModeImages,
    spotModeSocials,
    spotModeEntityType,
    setIsSubmittingSpot,
    spotModeStartTime,
    setSpotModeStartTime,
    spotModeLocationSetTime,
    setSpotModeLocationSetTime,
    userLocation,
    radius,
    fetchNearbyEntities,
    setEntities,
    spotModeCallback,
    isImmersiveMode,
    onTabChange,
    getUserLocation: triggerSpotGeolocation,
    t
  })

  // Spot mode activation wrapper
  const { handleSpotModeActivation } = useSpotModeActivation({
    user,
    isImmersiveMode,
    onTabChange,
    baseHandleSpotModeActivation,
    entityDetails
  })

  // Entity confirmation callback
  const handleEntityConfirmSuccess = useCallback(async () => {
    await fetchEntityLocations(null, null, null, true)
  }, [fetchEntityLocations])

  // Override spot submit for entity mode
  const handleSpotSubmit = useCallback(async () => {
    const success = await baseHandleSpotSubmit()
    if (!success) return

    if (isEntityMode && entityId) {
      await new Promise(resolve => setTimeout(resolve, 500))
      await fetchEntityLocations(entityId, null, null, true)
    }
  }, [baseHandleSpotSubmit, isEntityMode, entityId, fetchEntityLocations])

  // Return all state and handlers
  return {
    // Core state
    t,
    i18n,
    navigate,
    location,
    user,
    isAuthenticated,
    logout,
    convertFromKm,
    isEntityMode,
    
    // Map state
    entities,
    userLocation,
    setUserLocation,
    radius,
    setRadius,
    searchLocation,
    setSearchLocation,
    searchLocationName,
    setSearchLocationName,
    showUserLocation,
    setShowUserLocation,
    isLoading,
    mapRef,
    markerRefs,
    currentOpenPopupRef,
    
    // Entity mode state
    entityGallery,
    setEntityGallery,
    galleryRenderKey,
    refreshGallery,
    confirmingSighting,
    setConfirmingSighting,
    
    // Favorites
    isFavorite,
    toggleFavorite,
    isTogglingFavorite,
    favorites,
    updateFavoriteNotify,
    
    // Location state
    locationSource,
    isGeocoding,
    
    // Entity selection
    entitySearchQuery,
    setEntitySearchQuery,
    selectedEntityId,
    setSelectedEntityId,
    focusedEntityId,
    setFocusedEntityId,
    isolatedEntityId,
    setIsolatedEntityId,
    
    // Panel state
    isSpotPanelMinimized,
    setIsSpotPanelMinimized,
    isSearchPanelMinimized,
    setIsSearchPanelMinimized,
    initialChannelId,
    
    // Search
    entitySearchResults,
    isSearching,
    showSearchDropdown,
    setShowSearchDropdown,
    
    // Spot mode
    isSpotModeActive,
    spotModeLocation,
    setSpotModeLocation,
    spotModeEntity,
    setSpotModeEntity,
    spotModeEntityType,
    setSpotModeEntityType,
    spotModeNotes,
    setSpotModeNotes,
    spotModeEntityStatus,
    setSpotModeEntityStatus,
    spotModeSocials,
    setSpotModeSocials,
    spotModeImages,
    setSpotModeImages,
    isSubmittingSpot,
    spotModeStartTime,
    deactivateSpotMode,
    
    // Forms
    loginFormData,
    loginErrors,
    handleLoginChange,
    handleLoginSubmit,
    registerFormData,
    registerErrors,
    handleRegisterChange,
    handleRegisterSubmit,
    isAuthLoading,
    googleAuth,
    contactFormData,
    contactErrors,
    isContactSubmitting,
    contactSuccess,
    handleContactChange,
    handleContactSubmit,
    setContactSuccess,
    
    // Confirmations
    pendingConfirmations,
    loadingConfirmations,
    confirmingId,
    loadPendingConfirmations,
    handleConfirmSighting: handleConfirmSightingBase,
    
    // Stats
    stats,
    statsLoading,
    refetchStats,
    
    // Map configuration
    defaultCenter,
    mapCenter,
    mapZoom,
    mapBounds,
    
    // Filters
    filteredEntities,
    activeFilters,
    handleFilterToggle,
    openNowActive,
    toggleOpenNow,
    
    // Handlers
    handleRadiusChange,
    geocodeAddress,
    handleSelectEntity,
    handleCurrentLocation,
    handleSpotModeActivation,
    handleSpotSubmit,
    handleEntityConfirmSuccess,
    fetchEntityLocations,
    activeMapLocation
  }
}
